import async from 'async';
import { MessagePlugin, NotifyPlugin, NotificationInstance } from 'tdesign-vue';
import { ComponentInternalInstance, computed, getCurrentInstance, ref, Ref } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入工具类
import { PAY_SOURCE, openTeamPayDialog } from '@/renderer/scripts/composition/dialogHandler';
import downloadTaskManager, {
  handleDownloadFilesToTask,
  getDownloadUrl,
} from '@/renderer/scripts/utils/download/index';
import ipc from '@/renderer/scripts/utils/ipc';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { Task } from '@/renderer/scripts/utils/TaskManager/types';
import { DownloadFileInfo, TeamPathInfo } from '@/renderer/scripts/utils/download/types';
import mkdir, { downloadByATag } from '@/renderer/scripts/utils/download/utils';
import storage from '@/renderer/scripts/utils/storage/index';
import store from '@/renderer/store/index';
import smh, { SMH } from '@/renderer/scripts/smh';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import shareAccessTokenManager from '@/renderer/scripts/utils/AccessTokenManager/share';
import { checkVirusStatus } from '@/renderer/scripts/utils/checkVirusStatus';
import { httpUrl, HttpUrl } from '@/renderer/scripts/http';
import { getDownloadPath } from '@/renderer/scripts/utils/download/downloadHelper';
import { transformErrorForSerialization } from '@/common/tools';
import { AsyncTaskParams } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import { IS_BROWSER } from '@/common/constant/platform';
import errorCodeMap from '@/renderer/scripts/utils/errorCode';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';

let fs: typeof import('fs');
let path: typeof import('path');
let electron: typeof import('electron');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
  electron = (window.parent as any).require('electron') as typeof import('electron');
}
interface FileItem {
  creationTime: string;
  id: number;
  modificationTime: string;
  name: string;
  path: string[];
  type: string;
  userId: string;
}
interface DownloadResult {
  secret: string;
  sign: string;
}
type FailInfo = {
  list: unknown[];
  type: string;
};

type Handler = {
  methods: {
    prepareDownload: (downloadData: Array<SMH.SpaceFileItemData>, shareToken?: string) => void;
    listDownloadItems: (
      item: SMH.SpaceFileItemData,
      saveRelPath: string,
      fileOrFolderName: string,
      saveRootFolderName: string,
    ) => void;
    downloadSingleFileInBrowser: (fileName: string, filePath: string) => void;
  };
};
type TempFileInfo = {
  folder: Array<DownloadFileInfo>;
  file: Array<DownloadFileInfo>;
};
type TaskInfo = {
  id: number;
  space: string; // 还需要记录当前的spaceId，防止多个下载文件的时候请求出错
  notify: Promise<NotificationInstance>; // 记录对应的通知组件
};
// filePath: string; // 文件在smh中的绝对路径，主要用与获取下载信息
// saveFileRelativePath?: string; // 文件保存时的相对路径，即完整路径剥离下载选择目录后的路径，用于下载时在本地建立保存路径

export default function handler(spaceId: Ref<SpaceId>): Handler {
  const vm = (getCurrentInstance() as ComponentInternalInstance).proxy;
  let fileMap: Record<string, DownloadFileInfo[]> = {};
  let folderMap: Record<string, DownloadFileInfo[]> = {};
  let tempFileMap: Record<string, TempFileInfo> = {};
  let downloadTarget = '';
  let shareToken = '';
  const sharePreviewerOrganizationId = computed(() => {
    const { pathname } = window.location;
    const queryFrom = vm.$route.query.from;
    return vm.$store.getters.orgId !== -1 && (pathname.includes('/web/share') || queryFrom === 'share')
      ? vm.$store.getters.orgId
      : undefined;
  });
  let routerName = '';
  let teamPathInfo!: TeamPathInfo;
  const currPath = ref('');
  let timer: number | undefined;
  // 记录当前taskId是否完成的
  const taskInfo: TaskInfo[] = [];
  const loadingTip = {
    render(id: number) {
      return (createElement: any) =>
        createElement('p', {}, [
          i18n_t('正在打包中,请稍后...'),
          createElement(
            'span',
            {
              style: {
                color: '#0444D9',
                cursor: 'pointer',
                'padding-left': '24px',
              },
              on: {
                click() {
                  // 清除对应的数据和关闭组件
                  taskInfo.forEach((item: TaskInfo, index: number) => {
                    if (item.id === id) {
                      taskInfo.splice(index, 1);
                      NotifyPlugin.close(item.notify);
                    }
                  });
                },
              },
            },
            i18n_t('取消下载'),
          ),
        ]);
    },
  };
  const methods = {
    async downloadSingleFileInBrowser(
      fileName: string,
      filePath: string,
      newWin?: boolean,
      historyId?: number,
      shareToken?: string,
      spaceOrgId?: number,
    ) {
      let fileInfo: any;
      try {
        const temp = {
          filePath,
          contentDisposition: 'attachment',
          spaceId: spaceId.value,
          shareToken,
          purpose: 'download',
          collectToken: store.getters.collectToken,
          ...(sharePreviewerOrganizationId.value && {
            sharePreviewerOrganizationId: sharePreviewerOrganizationId.value,
          }),
        };
        historyId &&
          Object.assign(temp, {
            historyId,
          });
        spaceOrgId &&
          Object.assign(temp, {
            spaceOrgId,
          });
        // 分享链接处的下载统一返回cosUrl, 不再走302重定向
        fileInfo = await smh.commonAPI.getDownloadCosUrl(temp as SMH.FileParams);
        downloadByATag(fileInfo.data.cosUrl, { fileName, newWin });
      } catch (error: any) {
        if (error?.code === 'NoEnoughRemainingTrafficQuota') {
          if (vm.$route.name === 'collaborationSpaceList' || vm.$route.name === 'collaborationSpace') {
            openTeamPayDialog(PAY_SOURCE.DOWNLOAD_FILE_TRAFFIC, {
              isCollaboration: true,
              collaborationRole: vm.$route.params.isOwner,
            });
          } else {
            openTeamPayDialog(PAY_SOURCE.DOWNLOAD_FILE_TRAFFIC);
          }
        } else {
          tips.error(error as MsgError);
        }
      }
    },
    // 下载前的准备，如列出文件夹及文件，选择路径，重置数据
    async prepareDownload(downloadData: Array<SMH.SpaceFileItemData>, _shareToken?: string) {
      currPath.value = (downloadData[0].path as string[]).slice(0, -1).join('/');
      fileMap = {};
      folderMap = {};
      tempFileMap = {};
      shareToken = _shareToken || '';
      // 多选的检测中的文件、病毒文件、敏感文件前端过滤掉;文件夹中的文件由后端过滤
      const data: SMH.SpaceFileItemData[] = downloadData.filter(item => {
        // 网盘初始化文件无userId(检测中允许下载),其他文件(检测中允许上传者下载)
        const isSelfOrDefault = Number(item.userId) === store.getters.userId || !item.userId;
        return (
          item.authorityList.canDownload &&
          item.virusAuditStatus !== 3 &&
          item.sensitiveWordAuditStatus !== 2 &&
          (item.virusAuditStatus !== 1 || isSelfOrDefault)
        );
      });
      if (data.length === 0) {
        tips.error(i18n_t('当前所选项暂不支持下载'), 5000);
        return;
      }
      const routerName = vm.$route.name;
      if (routerName !== 'workbench') {
        // 在这里可以统一记录多文件下载的情况上报
        reporter.report('download', {
          download_location: routerName,
          download_mode: downloadData.length > 1 ? 'batch_download' : 'single_download',
          batch_num: data.length,
          downloadData: data,
          file_name: data.length === 1 ? data[0]?.name : 'BATCH_DOWNLOAD',
        });
      }
      if (IS_BROWSER) {
        // 单个文件直接下载
        const [firstFile] = data;
        if (data.length === 1 && firstFile?.type === 'file') {
          const fileName = firstFile.name;
          const filePath = (firstFile.path as string[]).join('/');
          const spaceOrgId = firstFile?.spaceOrgId;
          this.downloadSingleFileInBrowser(fileName, filePath, false, firstFile.historyId, shareToken, spaceOrgId);
        } else {
          // 使用后台接口下载多个文件和文件夹
          const requestBody: string[] = [];
          data.forEach((item: any) => {
            requestBody.push(item.path.join('/'));
          });
          this.checkTimer(requestBody);
        }
      } else {
        downloadTarget = await getDownloadPath();
        if (!downloadTarget) return;
        await this.arrangeDownloadData(data);
      }
    },
    async checkTimer(requestBody: string[]) {
      try {
        const res = await smh.commonAPI.batchDownload(
          {
            spaceId: spaceId.value as string,
            shareToken,
            ...(sharePreviewerOrganizationId.value && {
              sharePreviewerOrganizationId: sharePreviewerOrganizationId.value,
            }),
          },
          {
            body: requestBody,
          },
        );
        // 说明正在异步打包，需要查询状态
        if (res.status === 202) {
          window.clearInterval(timer);
          const msg = NotifyPlugin.info({
            title: i18n_t('下载准备中...'),
            content: loadingTip.render(res.data.taskId as number),
            duration: 0,
            closeBtn: true,
            onCloseBtnClick() {
              taskInfo.forEach((item: TaskInfo, index: number) => {
                item.id === res.data.taskId && taskInfo.splice(index, 1);
              });
            },
          });
          // 需要将taskId记录
          taskInfo.push({
            id: res.data.taskId as number,
            space: spaceId.value,
            notify: msg,
          });
          timer = window.setInterval(async () => {
            for (let i = 0; i < taskInfo.length; i++) {
              /* eslint-disable no-await-in-loop */
              const asyncRes = await this.asyncTaskQuery({
                taskIdList: taskInfo[i].id,
                shareToken,
                spaceId: taskInfo[i].space,
              });
              // 完成下载
              if (
                asyncRes &&
                asyncRes.data[0]?.status === 200 &&
                String(asyncRes.data[0]?.taskId) === String(taskInfo[i].id)
              ) {
                this.batchDownloadFileInBrowser(asyncRes.data[0].result, taskInfo[i].space);
                NotifyPlugin.close(taskInfo[i].notify);
                taskInfo.splice(i, 1);
              }
              /**
               * 下载异常
               * case1: 后端返回 5xx
               * case2: 明确错误码，比如文件数量超过限制（后端限制1w条）
               */
              if (asyncRes && asyncRes.data[0] && String(asyncRes.data[0]?.taskId) === String(taskInfo[i].id)) {
                const item = asyncRes.data[0];
                if (item.status && Math.floor(item.status / 100) === 5) {
                  // 后端返回 5xx
                  NotifyPlugin.close(taskInfo[i].notify);
                  taskInfo.splice(i, 1);
                  tips.error(i18n_t('下载失败，请稍后重试'));
                } else if (item?.result?.code) {
                  // 后端返回错误码
                  NotifyPlugin.close(taskInfo[i].notify);
                  taskInfo.splice(i, 1);
                  tips.error({ code: item.result.code, message: item.result.message, status: item.result.status });
                }
              }
              if (taskInfo.length === 0) {
                window.clearInterval(timer);
                timer = undefined;
                NotifyPlugin.closeAll();
                break;
              }
            }
          }, 2000);
          return;
        }
        if (res.status === 200) {
          this.batchDownloadFileInBrowser(res.data, spaceId.value);
        }
      } catch (err: any) {
        if (err?.code === 'NoEnoughRemainingTrafficQuota') {
          if (vm.$route.name === 'collaborationSpaceList' || vm.$route.name === 'collaborationSpace') {
            openTeamPayDialog(PAY_SOURCE.DOWNLOAD_FILE_TRAFFIC, {
              isCollaboration: true,
              collaborationRole: vm.$route.params.isOwner,
            });
          } else {
            openTeamPayDialog(PAY_SOURCE.DOWNLOAD_FILE_TRAFFIC);
          }
        } else {
          tips.error(err as MsgError);
        }
      }
    },
    async asyncTaskQuery(params: AsyncTaskParams) {
      try {
        const res = await smh.commonAPI.asyncTaskQuery(params);
        return res;
      } catch (err) {
        tips.error(err as MsgError);
        return null;
      }
    },
    // 对后台返回的数据进行处理然后执行下载
    async batchDownloadFileInBrowser(data: DownloadResult, space: string) {
      const { libraryId } = await (shareToken ? shareAccessTokenManager.get(shareToken) : spaceATM.get(space));
      const SpaceId = space || (await spaceATM.getPersonalSpaceIdAsync());
      const domainUrl = !store.getters.isPrivate
        ? `https://dl${HttpUrl.getHostPrefix()}.tencentsmh.cn`
        : httpUrl.SMHUrl;
      const prefixUrl = `${domainUrl}/batch-download`;
      const url = `${prefixUrl}/${libraryId}/${SpaceId}/${data.secret}.zip?sign=${data.sign}`;
      downloadByATag(url);
    },
    // 选择下载保存路径
    async arrangeDownloadData(downloadData: Array<SMH.SpaceFileItemData>) {
      const msg = MessagePlugin.loading(i18n_t('正在整理文件'), 0);
      try {
        await this.loadDownloadData(downloadData);
        MessagePlugin.close(msg);
        let totalFileCount = 0;
        // 将tempFileMap中对象的folder前置，file后置，放入fileMap中，便于先创建文件夹，再下载文件
        Object.keys(tempFileMap).forEach(key => {
          fileMap[key] = [];
          folderMap[key] = [];
          fileMap[key] = fileMap[key].concat(tempFileMap[key].file);
          folderMap[key] = folderMap[key].concat(tempFileMap[key].folder);
          totalFileCount = totalFileCount + fileMap[key].length;
          fileMap[key].length === 0 && delete fileMap[key];
          folderMap[key].length === 0 && delete folderMap[key];
        });
        if (totalFileCount > ConstantConfig.FILE_COUNT_LIMIT) {
          if (downloadData.length > 1) {
            throw new Error('BulkFolderLimitReached');
          } else {
            throw new Error('MaxFileLimitReached');
          }
        } else {
          this.downloadFiles();
        }
      } catch (error: any) {
        const err = transformErrorForSerialization(error);
        const code = err.code || 'ERR_BAD_RESPONSE';
        const failInfo: FailInfo = { list: [], type: 'download' };
        downloadData.forEach(item => {
          failInfo.list.push({
            path: item.path.join('/'),
            error: code,
            msg: errorCodeMap[code as string],
          });
        });
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ERRINFO_SHOW, failInfo);
      }
    },
    // 1. 加载所有选中的文件夹里面的内容，以及选中的文件夹和文件
    async loadDownloadData(downloadData: Array<SMH.SpaceFileItemData>) {
      routerName = vm.$route.name as string;
      if (routerName === 'teamSpace') {
        teamPathInfo = storage.get(storage.StoreKey.TEAM_PATH) as TeamPathInfo;
      } else if (routerName === 'collaborationSpaceList') {
        const { spaceId, id, name, SpaceOrgId, path } = vm.$route.query;
        teamPathInfo = {
          path: (currPath.value as string).split('/'),
          spaceId: spaceId as string,
          orgId: +SpaceOrgId,
          groupId: +id,
          name: name as string,
        };
      }
      try {
        await async.eachLimit(downloadData, 16, (ele, next) => {
          const basePath = [currPath?.value, ele.name].filter(v => v).join('/');
          const legalEleName = ele.name.replaceAll(/[\\/:*?"<>|]/g, '_');
          const saveFileRelativePath = ele.type === 'dir' ? this.handleRenameFolder(legalEleName) : legalEleName;
          const saveRootFolderName = saveFileRelativePath;
          tempFileMap[ele.name] = {
            folder: [],
            file: [],
          };
          if (ele.type !== 'dir') {
            tempFileMap[ele.name].file.push({
              ...ele,
              size: Number(ele.size),
              filePath: basePath,
              saveFileRelativePath,
              saveRootFolderName,
              downloadTarget,
              spaceId: spaceId?.value,
              historyId: ele.historyId,
              routerName,
              teamPathInfo,
            });
            next();
          } else {
            tempFileMap[ele.name].folder.push({
              ...ele,
              size: Number(ele.size),
              filePath: basePath,
              saveFileRelativePath,
              saveRootFolderName,
              downloadTarget,
              spaceId: spaceId?.value,
              routerName,
              teamPathInfo,
            });
            this.listDownloadItems(ele, saveFileRelativePath, ele.name, saveRootFolderName)
              .then(() => next())
              .catch(error => next(error));
          }
        });
      } catch (error: any) {
        throw error;
      }
    },
    // 2. 列出最顶层的文件夹下面的所有文件及文件夹，并push到对应数组中去
    async listDownloadItems(
      item: SMH.SpaceFileItemData,
      saveRelPath: string,
      fileOrFolderName: string,
      saveRootFolderName: string,
    ) {
      const basePath = [currPath?.value, fileOrFolderName].join('/');
      try {
        const totalListOfCurrPath = await this.listAllItemsOfCurrPath(item);
        await async.eachLimit(totalListOfCurrPath, 16, (ele, next) => {
          const filePath = [`${item.filePath ? item.filePath : basePath}`, ele.name].join('/');
          const saveFileRelativePath = [
            `${item.saveFileRelativePath ? item.saveFileRelativePath : saveRelPath}`,
            ele.name.replaceAll(/[\\/:*?"<>|]/g, '_'),
          ].join('/');
          if (ele.type === 'dir') {
            tempFileMap[fileOrFolderName].folder.push({
              ...ele,
              filePath,
              saveFileRelativePath,
              saveRootFolderName,
              downloadTarget,
              spaceId: spaceId?.value,
              routerName,
              teamPathInfo,
            });
            this.listDownloadItems(
              {
                ...ele,
                filePath,
                saveFileRelativePath,
              },
              saveRelPath,
              fileOrFolderName,
              saveRootFolderName,
            )
              .then(() => {
                next();
              })
              .catch(error => {
                next(error);
              });
          } else {
            tempFileMap[fileOrFolderName].file.push({
              ...ele,
              filePath,
              saveFileRelativePath,
              saveRootFolderName,
              downloadTarget,
              size: Number(ele.size),
              spaceId: spaceId?.value,
              historyId: ele.historyId,
              routerName,
              teamPathInfo,
            });
            next();
          }
        });
      } catch (error: any) {
        throw error;
      }
    },
    // 3. 列出当前层级路径下面的所有文件及文件夹，并返回
    async listAllItemsOfCurrPath(item: SMH.SpaceFileItemData) {
      const dirPath = item.filePath ? item.filePath : [currPath?.value, item.name].join('/');
      let currPathList: any[] = [];
      let currPage = 1;
      const listPerPage = async (page?: number): Promise<any> => {
        try {
          const { data: listRes } = await smh.commonAPI.listDirectory({
            dirPath,
            page,
            pageSize: 1000, // 默认每页1000条
            spaceId: spaceId?.value || '',
          });
          currPathList = currPathList.concat(listRes.contents);
          if (currPathList.length < listRes.totalNum) {
            currPage += 1;
            await listPerPage(currPage);
          }
        } catch (error: any) {
          logger.ui.debug('listPerPage', transformErrorForSerialization(error));
          throw error;
        }
      };
      await listPerPage(currPage);
      return currPathList;
    },
    handleRenameFolder(folderName: string) {
      let renameFolder = folderName;
      let savePath = path.resolve(downloadTarget, `${folderName}`);
      let i = 1;
      while (i <= 100000 && fs.existsSync(savePath)) {
        renameFolder = `${folderName} (${i})`;
        savePath = path.resolve(downloadTarget, `${renameFolder}`);
        i++;
      }
      return renameFolder;
    },
    async downloadFiles() {
      // 在本地根据文件夹系统创建目录
      folderMap && (await this.createDir());
      // 将所有文件加入下载列表中
      const Tasks: Task<DownloadFileInfo>[] = await handleDownloadFilesToTask(fileMap);
      async.forEachLimit(
        Tasks,
        2,
        (task, next) => {
          downloadTaskManager.addTask(task);
          next();
        },
        () => {
          downloadTaskManager.start();
        },
      );
      ipc.emit(ipc.RENDERER_EVENTS_MAP.WEB_TRANS_SHOW, {
        show: IS_BROWSER,
        tab: 'download',
      });
      !IS_BROWSER && tips.info(i18n_t('已添加至传输列表'));
    },
    async createDir() {
      async.eachLimit(folderMap, 16, (item: Array<DownloadFileInfo>) => {
        for (let i = 0; i < item.length; i++) {
          const ele = item[i];
          const savePath = path.join(downloadTarget, ele.saveFileRelativePath);
          mkdir.mkdirDeepSync(savePath);
        }
      });
    },
  };
  return {
    methods,
  };
}
